/**
 * Live presence heartbeat (LIVE PRESENCE feature).
 *
 * Maintains one doc per user in the `presence` collection — the admin Live
 * monitor reads these to see who is in the app right now and what they are
 * looking at. Security rules let a signed-in user write ONLY their own
 * `presence/{uid}` doc, so this needs an authed user.
 *
 * Design (mirrors lib/activity/activityLogger.ts):
 *  - A module singleton holds the current user and the latest view fields.
 *  - `startPresence()` writes 'online' immediately and keeps it fresh with a
 *    heartbeat setInterval(PRESENCE_HEARTBEAT_MS). `stopPresence()` clears the
 *    interval and writes 'offline'.
 *  - `setPresenceView(...)` updates the current view fields and writes, debounced
 *    ~500ms so rapid navigation coalesces into one write.
 *  - `setPresenceState(...)` flips online/background/offline and writes.
 *  - Writes the full Presence object via setDoc. Transient (offline) failures
 *    are swallowed — the heartbeat will catch up on the next beat.
 *  - No-ops safely when there is no user set.
 */
import { doc, setDoc } from 'firebase/firestore';
import type { Presence, PresenceState } from '@mitang/shared';
import { paths, PRESENCE_HEARTBEAT_MS } from '@mitang/shared';
import { db } from './firebase';

const VIEW_DEBOUNCE_MS = 500;

/** The identity fields the manager needs to build a Presence doc. */
export interface PresenceProfile {
  uid: string;
  name: string;
  email?: string;
  companyName?: string;
  city?: string;
}

interface ViewFields {
  currentView?: string;
  currentLabel?: string;
  currentProductId?: string;
}

let currentProfile: PresenceProfile | null = null;
let currentState: PresenceState = 'offline';
let view: ViewFields = {};
let heartbeat: ReturnType<typeof setInterval> | null = null;
let viewTimer: ReturnType<typeof setTimeout> | null = null;

/** Assemble the full Presence doc from the current user + view + state. */
function buildPresence(state: PresenceState): Presence | null {
  if (!currentProfile) return null;
  const now = Date.now();
  return {
    uid: currentProfile.uid,
    name: currentProfile.name,
    ...(currentProfile.companyName ? { companyName: currentProfile.companyName } : {}),
    ...(currentProfile.city ? { city: currentProfile.city } : {}),
    state,
    lastSeen: now,
    ...(view.currentView ? { currentView: view.currentView } : {}),
    ...(view.currentLabel ? { currentLabel: view.currentLabel } : {}),
    ...(view.currentProductId ? { currentProductId: view.currentProductId } : {}),
    updatedAt: now,
  };
}

/** Write the current presence with the given state. No-op without a user. */
async function write(state: PresenceState): Promise<void> {
  const presence = buildPresence(state);
  if (!presence) return;
  try {
    await setDoc(doc(db, paths.presence(presence.uid)), presence);
  } catch {
    // Offline / transient: the next heartbeat (or reconnect) will catch up.
  }
}

/**
 * Set/clear the user whose presence we write. Call on auth change.
 * Passing null clears the identity and marks the previous user offline.
 */
export function setPresenceUser(profile: PresenceProfile | null): void {
  if (profile) {
    currentProfile = profile;
    return;
  }
  // Clearing: tear down the heartbeat and mark offline before we forget who.
  if (heartbeat) {
    clearInterval(heartbeat);
    heartbeat = null;
  }
  if (viewTimer) {
    clearTimeout(viewTimer);
    viewTimer = null;
  }
  void write('offline');
  currentProfile = null;
  currentState = 'offline';
  view = {};
}

/** Start the heartbeat: write 'online' now, then refresh every heartbeat. */
export function startPresence(): void {
  if (!currentProfile) return;
  currentState = 'online';
  void write('online');
  if (heartbeat) return;
  heartbeat = setInterval(() => {
    void write(currentState);
  }, PRESENCE_HEARTBEAT_MS);
}

/** Stop the heartbeat and mark the user offline. */
export function stopPresence(): void {
  if (heartbeat) {
    clearInterval(heartbeat);
    heartbeat = null;
  }
  if (viewTimer) {
    clearTimeout(viewTimer);
    viewTimer = null;
  }
  currentState = 'offline';
  void write('offline');
}

/** Set the presence state (online/background/offline) and write it. */
export function setPresenceState(state: PresenceState): void {
  currentState = state;
  void write(state);
}

/** Update the current view fields and write (debounced ~500ms). */
export function setPresenceView(next: {
  view: string;
  label: string;
  productId?: string;
}): void {
  view = {
    currentView: next.view,
    currentLabel: next.label,
    ...(next.productId ? { currentProductId: next.productId } : {}),
  };
  if (!currentProfile) return;
  if (viewTimer) clearTimeout(viewTimer);
  viewTimer = setTimeout(() => {
    viewTimer = null;
    void write(currentState);
  }, VIEW_DEBOUNCE_MS);
}
